import { useState, useEffect, useRef, useCallback } from "react";
import Swal from "sweetalert2";
import DashboardLayout from "../../common/DashboardLayout";
import httpServices from "../../../services/httpServices";
import ImpersonationServices from "../../../services/ImpersonationServices";
import "./impersonateUser.css";

/**
 * Impersonate a user — sysadmin only.
 *
 * Support has otherwise had to work out what a customer sees by reading code and
 * counting rows. A school administrator reported that "the due dates are
 * populating on their own no matter what I do to change them", and answering it
 * meant guessing at her screen for hours. This opens it.
 *
 * Read-only throughout: the session can look at anything and change nothing.
 */

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;

const ImpersonateUser = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [starting, setStarting] = useState(null);
  const [error, setError] = useState("");

  // Results can arrive out of order when someone types quickly; only the newest
  // query is allowed to render.
  const latest = useRef(0);

  const run = useCallback(async (q) => {
    const seq = ++latest.current;
    setSearching(true);
    try {
      const res = await httpServices.get(`/admin/search-users?query=${encodeURIComponent(q)}`);
      if (seq !== latest.current) return;
      setResults((res && res.users) || []);
      setSearched(true);
      setError("");
    } catch (err) {
      if (seq !== latest.current) return;
      setError(err?.response?.data?.error || "Search failed.");
      setResults([]);
    } finally {
      if (seq === latest.current) setSearching(false);
    }
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_CHARS) {
      latest.current += 1;          // cancel anything in flight
      setResults([]); setSearched(false); setSearching(false);
      return undefined;
    }
    const t = setTimeout(() => run(q), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, run]);

  async function viewAs(user) {
    const { value: reason } = await Swal.fire({
      title: `View as ${user.name || user.email}?`,
      input: "text",
      inputLabel: "Why? (recorded in the audit log)",
      inputPlaceholder: "e.g. checking a reported problem with due dates",
      html:
        '<div class="text-muted small text-start">'
        + "You will see their screens exactly as they do. The session is "
        + "<b>read-only</b> — you cannot change anything on their behalf — and it "
        + "expires automatically.</div>",
      showCancelButton: true,
      confirmButtonText: "View as user"
    });
    if (reason === undefined) return;

    setStarting(user.id); setError("");
    try {
      const started = await ImpersonationServices.start({
        userId: user.id,
        email: user.email,
        reason,
        name: user.name,
        schoolName: user.schoolName || null
      });
      window.location.href = started.landing;
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not start the session.");
      setStarting(null);
    }
  }

  const q = query.trim();

  return (
    <DashboardLayout role="admin" layoutType="topnav" activeMenu="User Management" showBreadcrumbs={false}>
      <div className="imp-page">
        <div className="imp-head">
          <h2>Impersonate User</h2>
          <p>
            Open a customer&apos;s account to see exactly what they see. The session is
            read-only, expires on its own, and is recorded against your account.
          </p>
        </div>

        <div className="imp-search">
          <input
            type="text"
            value={query}
            autoFocus
            placeholder="Start typing a name or email — e.g. satwood"
            onChange={(e) => setQuery(e.target.value)}
          />
          {searching ? <span className="imp-spin" aria-label="searching" /> : null}
        </div>

        {q.length > 0 && q.length < MIN_CHARS ? (
          <div className="imp-hint">Keep typing — at least {MIN_CHARS} characters.</div>
        ) : null}

        {error ? <div className="imp-alert">{error}</div> : null}

        {searched && results.length === 0 && !searching ? (
          <div className="imp-empty">No users matched “{q}”.</div>
        ) : null}

        {results.length > 0 && (
          <div className="table-responsive imp-results">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th><th>Email</th><th>Type</th><th className="text-end">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {results.map((u) => (
                  <tr key={u.id}>
                    <td><b>{u.name || "—"}</b></td>
                    <td>{u.email}</td>
                    <td><span className="imp-type">{u.type || u.role || "User"}</span></td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        disabled={starting === u.id}
                        onClick={() => viewAs(u)}
                      >
                        {starting === u.id ? "Starting…" : "View as"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="imp-note">
          <b>What you can and cannot do.</b> Every screen renders as it does for them.
          Any attempt to save, assign, delete or complete anything is refused — the point
          is to see the problem, not to fix it from inside their account. A banner stays
          at the top of every page until you exit, and the session ends on its own.
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ImpersonateUser;
